import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, Store, Shirt, Sofa, Flower, Gift } from 'lucide-react';

const ctaButtons = [
  {
    icon: Calendar,
    text: "Book & Schedule",
    onClick: () => window.open('https://calendly.com/udaadu', '_blank')
  },
  {
    icon: Building,
    text: "What Fits My Lot?",
    onClick: () => window.open('/landing', '_self')
  },
  {
    icon: DollarSign,
    text: "Get Price & ROI Report",
    onClick: () => window.open('/adu-calculator', '_self')
  },
  {
    icon: Home,
    text: "Our Portfolio",
    onClick: () => window.open('/our-work', '_self')
  },
  {
    icon: HelpCircle,
    text: "Our FAQ",
    onClick: () => window.open('/faq', '_self')
  }
];

const HowToUseADUAsBoutiqueRetailOrShowroom = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-boutique-retail-or-showroom" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Turn Your Product Passion into a Shop
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            With a backyard ADU, you can open a small retail studio, design showroom, or local pickup site without leasing storefront space.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Perfect For Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>Who This Is Perfect For</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Gift className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Crafters and handmade product sellers</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shirt className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Boutique clothing or accessories</span>
                </div>
                <div className="flex items-center gap-3">
                  <Sofa className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Furniture and decor showrooms</span>
                </div>
                <div className="flex items-center gap-3">
                  <Flower className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Floral designers or curated gift studios</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&h=400" 
                alt="Boutique Retail Showroom ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Layout Must-Haves Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Layout Must-Haves</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Display & Presentation</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Front glass door or signage</li>
                <li>• Clean white or neutral walls for display</li>
                <li>• Track lighting or pendant lights</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Business Operations</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Storage closet for inventory</li>
                <li>• POS-ready desk or table setup</li>
                <li>• Wi-Fi and electrical for devices</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Standout Advantages Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Standout Advantages</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Store className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Local Showcase</h3>
              <p className="text-muted-foreground">Display products IRL and offer local pickup</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Gift className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Private Appointments</h3>
              <p className="text-muted-foreground">Host styling appointments or tastings</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Shirt className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Event Ready</h3>
              <p className="text-muted-foreground">Perfect for press events or client walkthroughs</p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Benefits */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>Business Benefits</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <DollarSign className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>No Lease Costs</h3>
              <p className="text-muted-foreground">Skip expensive commercial rent and build equity</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Store className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Custom Design</h3>
              <p className="text-muted-foreground">Design the perfect space for your brand</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Home className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Work-Life Balance</h3>
              <p className="text-muted-foreground">Operate your business from home</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Launch Your Retail Dreams
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Create a beautiful showroom that showcases your products and grows your business.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: index === 0 ? 'white' : '#E4683B' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsBoutiqueRetailOrShowroom;